import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import dayjs from 'dayjs';

export const RESERVATIONS_OVERVIEW_MONTH_PARAM = 'month';
const MONTH_PARAM_FORMAT = 'YYYY-MM';

/**
 * Build the URL of a reservations overview page showing the month of the given date.
 *
 * @param {string} overviewRoute route of the (CMS or public) reservations overview
 * @param {string|dayjs.Dayjs|null|undefined} date any date within the month to show
 * @returns {string}
 */
export const reservationsOverviewUrl = (overviewRoute, date) => {
  const month = date ? dayjs(date) : null;

  if (!month?.isValid()) {
    return overviewRoute;
  }

  return `${overviewRoute}?${RESERVATIONS_OVERVIEW_MONTH_PARAM}=${month.format(MONTH_PARAM_FORMAT)}`;
};

/**
 * Selected month of the reservations overview, kept in the URL (?month=YYYY-MM),
 * so going back from the reservation form returns to the same month.
 *
 * @returns {[dayjs.Dayjs, function(dayjs.Dayjs): void]} first day of the selected month and its setter
 */
const useReservationsOverviewMonth = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const monthParam = searchParams.get(RESERVATIONS_OVERVIEW_MONTH_PARAM);

  const selectedDate = useMemo(() => {
    if (monthParam && /^\d{4}-\d{2}$/.test(monthParam)) {
      const parsedMonth = dayjs(`${monthParam}-01`);

      if (parsedMonth.isValid()) {
        return parsedMonth;
      }
    }

    return dayjs().startOf('month');
  }, [monthParam]);

  const setSelectedDate = useCallback(
    (date) => {
      setSearchParams(
        (previousParams) => {
          const nextParams = new URLSearchParams(previousParams);
          nextParams.set(
            RESERVATIONS_OVERVIEW_MONTH_PARAM,
            dayjs(date).format(MONTH_PARAM_FORMAT),
          );
          return nextParams;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  return [selectedDate, setSelectedDate];
};

export default useReservationsOverviewMonth;
